'use client';

import React, { useMemo } from 'react';
import classnames from 'classnames';
import Typography from '../../../atoms/Typography/Typography';
import Box from '../../../atoms/Box/Box';
import type { TableFilterCardItem } from '../types';
import styles from './FilterCards.module.scss';

export type FilterCardsProps<T extends Record<string, unknown>> = {
  /** Full data array — counts are computed from this */
  data: T[];
  /** Column key to aggregate by */
  columnKey: string;
  /** Explicit card items. If omitted, derived from unique values in data */
  items?: TableFilterCardItem[];
  /** Currently active filter value (null = "All" selected) */
  activeValue: string | null;
  /** Called with the clicked value, or null when "All" is clicked */
  onCardClick: (value: string | null) => void;
  /** Data test ID prefix */
  dataTestId?: string;
  /** Optional explicit total for the "All" card (overrides data.length) */
  totalCount?: number;
};

/**
 * Clickable filter cards that sit above a Table, aggregating row counts by a column value.
 * Clicking a card filters the table to show only rows matching that value.
 * @component
 * @param props.data - Full data array to aggregate counts from
 * @param props.columnKey - Column key to group by
 * @param props.items - Explicit card items (optional — auto-derived if omitted)
 * @param props.activeValue - The currently active filter value (null = no filter)
 * @param props.onCardClick - Callback with the selected card value (null = "All")
 * @param props.dataTestId - Test ID prefix
 * @returns {JSX.Element}
 * @example
 * <FilterCards
 *   data={rows}
 *   columnKey="status"
 *   items={[{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]}
 *   activeValue={null}
 *   onCardClick={(v) => console.log(v)}
 *   dataTestId="order-filter"
 * />
 */
const FilterCards = <T extends Record<string, unknown>>({
  data,
  columnKey,
  items,
  activeValue,
  onCardClick,
  dataTestId = 'filter-cards',
  totalCount: propTotalCount,
}: FilterCardsProps<T>) => {
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    data.forEach((row) => {
      const val = String(row[columnKey] ?? '');
      map[val] = (map[val] ?? 0) + 1;
    });
    return map;
  }, [data, columnKey]);

  const cardItems = useMemo<TableFilterCardItem[]>(() => {
    if (items && items.length > 0) return items;
    return Object.keys(counts).map((val) => ({ value: val, label: val }));
  }, [items, counts]);

  const totalCount = propTotalCount ?? data.length;

  const handleKeyDown = (e: React.KeyboardEvent, value: string | null) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onCardClick(value);
    }
  };

  return (
    <Box className={styles.filterCards} data-testid={dataTestId}>
      {/* "All" card */}
      <Box
        className={classnames(styles.card, {
          [styles.active]: activeValue === null,
        })}
        data-testid={`${dataTestId}-all`}
        onClick={() => onCardClick(null)}
        onKeyDown={(e) => handleKeyDown(e, null)}
        role="button"
        tabIndex={0}
        aria-pressed={activeValue === null}
      >
        <Typography variant="label" size="medium" theme="dark" color="primary-1">
          All
        </Typography>
        <Typography variant="subtitle" size="medium" theme="dark" color="white" as="span">
          {totalCount}
        </Typography>
      </Box>

      {cardItems.map((item) => {
        const count = item.count ?? counts[item.value] ?? 0;
        const isActive = activeValue === item.value;

        return (
          <React.Fragment key={item.value}>
            <Box
              className={classnames(styles.card, {
                [styles.active]: isActive,
              })}
              data-testid={`${dataTestId}-${item.value}`}
              onClick={() => onCardClick(isActive ? null : item.value)}
              onKeyDown={(e) => handleKeyDown(e, isActive ? null : item.value)}
              role="button"
              tabIndex={0}
              aria-pressed={isActive}
            >
              <Typography variant="label" size="medium" theme="dark" color="primary-1">
                {item.label}
              </Typography>
              <Typography variant="subtitle" size="medium" theme="dark" color="white" as="span">
                {count}
              </Typography>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default FilterCards;
